import React, { useMemo } from 'react';
import {
    buildMatchingDropdownOptions,
    fetchLookupOptions,
    MASTERDATA_BROWSER_KEY,
    MASTERDATA_DEFAULT_BROWSER_KEY,
    renderArticleIdOption,
} from './useLookup';
import { useTranslation } from 'react-i18next';
import AutoCompleteField from '../../core/components/Fields/AutoCompleteField';
import { snakeCase } from 'lodash';
import { useApolloClient } from '@apollo/client';
import { createDropdownMessageBuilder } from '../../masterdata/utils.tsx';
import { format } from 'date-fns';
import useForceSelectionLookupProps, { useForceSelectionReselectEventListener } from './useForceSelectionLookupProps';
import { FieldProps, useFieldProps } from '../pages/Assistance/customizable/Field';
import { useAssistanceFieldContext } from '../pages/Assistance/AssistanceFieldContext';
import { FormFieldConfig, MatchingLookupProps } from '../pages/Assistance/useDocumentConfig';
import { useExtractionSearchResults } from './useExtractionSearchResults';
import { useOrderLineItemsFallback } from './useOrderLineItemsFallback';

const useLookupMatchingProps = ({
    config,
    lookupType,
    partitionId,
    matchingDocumentNumber,
}: {
    config: FormFieldConfig;
    lookupType: string;
    partitionId: string;
    matchingDocumentNumber: string;
}): Partial<MatchingLookupProps> => {
    let { lookupDefinitionIdDefault, lookupFieldNameDefault, lookupLastSuccessfulImportDate, documentNumberFieldName } =
        config;

    const extraSearchTerms = useMemo(
        () => ({ [snakeCase(documentNumberFieldName)]: matchingDocumentNumber }),
        [documentNumberFieldName, matchingDocumentNumber]
    );

    lookupLastSuccessfulImportDate = lookupLastSuccessfulImportDate
        ? format(
              new Date(lookupLastSuccessfulImportDate),
              localStorage.getItem('i18nextLng') === 'de' ? 'do MMMM, HH:mm' : 'MMMM do, HH:mm'
          )
        : '';

    const fetchOptions = fetchLookupOptions({
        client: useApolloClient(),
        lookupDefinitionId: lookupDefinitionIdDefault,
        lookupFieldName: lookupFieldNameDefault,
        partitionId: partitionId,
    });

    const renderOptions = (items, searchTerm, hasMoreResults) => {
        /*
        Render the options to display in the dropdown list for matching use case:
          - Add a menu item indicating the lookup name where the data comes from
          - Add the MD items to display if any, otherwise add a dropdown message
          - When the matched document has more items than fit in the dropdown, add a button to browse them
          - Add a button to open up and browse in the entire MD
         */
        const { t } = useTranslation('masterdata');
        const lookupTypeLocalizationKey = snakeCase(lookupType?.replace('Type', '') || '').toUpperCase();
        const labelName = t(`masterdata:browser.dropDownTitle.` + lookupTypeLocalizationKey);

        const dropdownMessage = createDropdownMessageBuilder({
            partitionId: partitionId,
            isPartitionIdField: false,
            hasMatching: true,
            matchingDocumentNumber: matchingDocumentNumber,
            lookupLastSuccessfulImportDate: lookupLastSuccessfulImportDate,
            targetField: lookupFieldNameDefault,
            lookupType: lookupType,
        })(t, searchTerm, items);

        const browserButtons = [];
        if (hasMoreResults) {
            browserButtons.push({
                label: t(
                    lookupType === 'OfferMatchingLookupType'
                        ? 'browser.openBrowserOfferItems'
                        : 'browser.openBrowserOrderItems'
                ),
                value: MASTERDATA_BROWSER_KEY,
            });
        }
        browserButtons.push({ label: t('browser.searchEntireData'), value: MASTERDATA_DEFAULT_BROWSER_KEY });

        return buildMatchingDropdownOptions({
            headerLabel: labelName,
            items,
            message: dropdownMessage,
            browserButtons,
        });
    };

    return {
        hasMatching: true,
        extraSearchTerms,
        matchingDocumentNumber,
        lookupLastSuccessfulImportDate,
        fetchOptions,
        renderOptions,
        minSearchChars: 0,
    };
};

export const ArticleNumberField = (props: FieldProps) => {
    const { clientPartitionId, config, matchingDocumentNumber, fieldName } = useAssistanceFieldContext();
    const { lookupDefinitionId, lookupFieldName, allowInvalidIds, resetInvalidOnBlur } = config;

    const fieldProps = useFieldProps(props);
    const { value, onValueChange, onFocus, onBlur, onCommit } = fieldProps;

    const hasMatching = !!config?.documentNumberFieldName;

    // allow controlling the partition id per field via config
    const { partitionId: configPartitionId } = config;

    const matchingLookupType = config.lookupType || 'OrderMatchingLookupType';
    const lookupMatchingProps = useLookupMatchingProps({
        config,
        lookupType: matchingLookupType,
        partitionId: configPartitionId || clientPartitionId,
        matchingDocumentNumber,
    });

    const extractionSearch = useExtractionSearchResults({
        fieldName,
        lookupFieldName,
        value,
    });

    // Referenced order's line items, offered when no article on the order matches the current value
    // (OC / invoice matching fields only).
    const orderLineItemsFallback = useOrderLineItemsFallback({
        hasMatching,
        matchingLookupType,
        matchingDocumentNumber,
        lookupDefinitionId,
        partitionId: configPartitionId || clientPartitionId,
        extraSearchTerms: lookupMatchingProps.extraSearchTerms,
    });

    const forceSelectionLookupProps = useForceSelectionLookupProps({
        lookupType: hasMatching ? matchingLookupType : 'ArticleLookupType',
        lookupDefinitionId,
        lookupFieldName,
        partitionId: configPartitionId || clientPartitionId,
        allowInvalidIds,
        resetInvalidOnBlur,
        value,
        onValueChange,
        onUpdatePayloadChange: props.onUpdatePayloadChange,
        onFocus,
        onBlur,
        onCommit,
        isPartitionIdField: config?.isPartitionIdField === true,
        // only apply if matching is enabled (or if e.g. searchLookupOnEmptyValue is set in the config)
        lookupMatchingProps: hasMatching ? { ...config, ...lookupMatchingProps } : config,
        extractionSearchResults: extractionSearch.extractionSearchResults,
    });

    useForceSelectionReselectEventListener(props.inputRef, forceSelectionLookupProps.onValueChange);

    // The order-scoped live search has no real match when every option is a decoration (section
    // header / message / separator / browse button) rather than an actual lookup row.
    const hasLiveResults = (forceSelectionLookupProps.options || []).some((option: any) => !option.role);

    // When no article on the referenced order matches the current value, offer the order's line
    // items to pick from instead of a "no results" message, both on focus and while typing.
    const showOrderLineItemsOnNoMatch =
        orderLineItemsFallback.isEnabled &&
        !forceSelectionLookupProps.isLoading &&
        !hasLiveResults &&
        orderLineItemsFallback.orderLineOptions.length > 0;

    // When extraction search results should be shown, use them instead of live search results.
    // Otherwise, when the live search has no match, show the referenced order's line items.
    const displayOptions = useMemo(() => {
        if (extractionSearch.shouldShowStored) {
            return extractionSearch.extractionOptions;
        }
        if (showOrderLineItemsOnNoMatch) {
            return orderLineItemsFallback.orderLineOptions;
        }
        return forceSelectionLookupProps.options;
    }, [
        extractionSearch.shouldShowStored,
        extractionSearch.extractionOptions,
        showOrderLineItemsOnNoMatch,
        orderLineItemsFallback.orderLineOptions,
        forceSelectionLookupProps.options,
    ]);

    // Props below override values from the spread `forceSelectionLookupProps` (options, onValueChange, onFocus,
    // isLoading). Order matters: the explicit props must come after the spread to take effect.
    return (
        <AutoCompleteField
            {...fieldProps}
            {...forceSelectionLookupProps}
            options={displayOptions}
            onValueChange={(newValue: string) => {
                // Update extraction-search state first so the display precedence is correct before
                // the forceSelection handler triggers option rendering via its own state updates.
                extractionSearch.onInputChange(newValue);
                forceSelectionLookupProps.onValueChange(newValue);
            }}
            onFocus={(e: any) => {
                // Reset to the extraction-results view and load the order's line items (used as the
                // fallback when the live search finds no match) before forceSelection opens the dropdown.
                extractionSearch.onFocus();
                orderLineItemsFallback.loadOrderLines();
                forceSelectionLookupProps.onFocus(e);
            }}
            renderOption={renderArticleIdOption(lookupFieldName)}
            popoverMinWidth={hasMatching ? '20rem' : undefined}
            isLoading={
                extractionSearch.shouldShowStored
                    ? false
                    : orderLineItemsFallback.isLoading || forceSelectionLookupProps.isLoading
            }
        />
    );
};

const articleNumberFieldNames = [
    'article_number',
    'article_number_2',
    'article_number_3',
    'article_partition_specific_number',
    'article_description',
    'number', // used by ListOfServices
    'description', // used by ListOfServices
];

export const ArticleNumberFieldConditionalComponents = [
    {
        // article number field is only a lookup field if it has a lookupFieldName and lookupDefinitionId
        condition: ({ fieldName, config }) =>
            articleNumberFieldNames.includes(fieldName) && config.lookupFieldName && config.lookupDefinitionId,
        component: ArticleNumberField,
    },
];
